import { useEffect, useState, useCallback, useMemo } from 'react';
import { once } from '../../utils';
import { useTrackChange } from '../trackChange';

/**
 * Analytic video events adapter
 * it simplifies video events for analytic use.
 */
export const useAnalyticAdapter = (videoRef, callbacks) => {
    const [played, setPlayed] = useState(false);

    const onPlay = useMemo(
        () =>
            once(() => {
                callbacks.onPlay();
                setPlayed(true);
            }),
        [callbacks]
    );

    const onEnded = useMemo(
        () =>
            once(() => {
                if (played) {
                    callbacks.onWatched();
                }
            }),
        [callbacks, played]
    );

    useTrackChange(
        videoRef,
        useCallback(
            (locale) => {
                callbacks.onSubtitleChange(locale);
            },
            [callbacks]
        )
    );

    useEffect(() => {
        const video = videoRef.current;

        video?.addEventListener('play', onPlay);

        return () => video?.removeEventListener('play', onPlay);
    }, [videoRef, onPlay]);

    useEffect(() => {
        const video = videoRef.current;

        video?.addEventListener('ended', onEnded);

        return () => video?.removeEventListener('ended', onEnded);
    }, [videoRef, onEnded]);

    useEffect(() => onEnded, [onEnded]);

    useEffect(() => {
        const onVisibilyChange = () => {
            /**
             * We consider few options to track user stop of interaction with a video
             * - on page close
             * - on visibility change
             *
             * We took visibility change way because
             * - recomended by mdn
             * - mobile friendly - close page might not be fired once mobile close task on background.
             *
             * Considering hidden state as user stopped to watch a video
             */
            if (document.visibilityState === 'hidden') {
                onEnded();
            }
        };

        document.addEventListener('visibilitychange', onVisibilyChange);

        return () => document.removeEventListener('visibilitychange', onVisibilyChange);
    }, [onEnded]);
};
